import React, {
	useState,
	useCallback,
	Suspense,
	lazy,
} from 'react'
import * as ls from 'utils/localStorage'

import { ResumeViewProps } from '.'
import {
	Container,
	LottieLoaderHolder,
	LottieLoader,
	BottomBlur,
	TextContainer,
	UploadButton, DownloadButton, FixedButtonHolder,
} from './ResumeView.styles'
import Popup from 'components/Popup'
import UploadWarning from 'components/Popups/UploadWarning'

// Lazy-load react-pdf so the ~1.5 MB preview renderer + pdf.worker only ship
// when a user actually views a resume (hygienist profile, DO candidate page,
// etc.). The webpack chunk name keeps the split bundle recognisable.
const ResumePdfRenderer = lazy(() =>
	import(/* webpackChunkName: "resume-pdf-renderer" */ './ResumePdfRenderer'),
)

const { FilePicker } = require('react-file-picker')

declare let window: any

export default function ResumeView(
	{
		file,
		onUpload,
		onDownload,
		externalLoading,
		name,
		...props
	}: ResumeViewProps,
) {
	const [pagesCount, setPagesCount] = useState(0)
	const [isLoading, setLoading] = useState(!!file)
	const [uploadWarningShown, setUploadWarningShown] = useState(false)
	const [uploadErrorShown, setUploadErrorShown] = useState(false)
	const userAccount: { hygienistId: number, dentist?: any, hygienist?: any } = ls.get('userAccount') || {}
	const role = Number.isInteger(userAccount.hygienistId) ? 'Hygienist' : 'Dentist'

	const getErrorType = (error: string) => {
		if(error){
			return error.split(' ')[0] === 'Must' ? 'extension' : 'size';
		}
	}
	return (
		<Container {...props}>
			{file ? (
				<Suspense fallback={null}>
					<ResumePdfRenderer
						file={file}
						pageCount={pagesCount}
						variant='default'
						onLoadSuccess={({ numPages }) => {
							setPagesCount(numPages)
							setLoading(false)
						}}
						error={
							role === 'Dentist'
								? 'Only resumes uploaded in PDF formats can be previewed here. Please download the resume to view it.'
								: `Preview is only available for PDF files. 
						You have a resume uploaded, but it's just in a file format that can't be previewed here. 
						If you want to upload a new resume, click "Update Resume" below.`
						}
						onSourceError={() => setLoading(false)}
						onLoadError={(err: any) => {
							console.log(err)
							return setLoading(false)
						}}
					/>
				</Suspense>
			) : (
				<TextContainer>
					Please upload a resume to be able to submit an application to jobs.
					Resumes need to be uploaded in one of the following formats: JPG, PNG,
					PDF, TIFF or Word Documents.
				</TextContainer>
			)}
			<BottomBlur />
			<FixedButtonHolder isResume={pagesCount > 0}>
				{onDownload && !isLoading &&(
					<DownloadButton onClick={onDownload}>
						Download{' '}
						{(name as string).split(' ').length > 1 ?
						(name as string).split(' ')[0]
						: name}'s
						Resume
					</DownloadButton> //return only first name
				)}
				{onUpload &&
					(window.cordova ? (
						<UploadButton onClick={() => setUploadWarningShown(true)}>
							{!!file ? 'Update resume' : 'Upload Word Document or PDF'}
						</UploadButton>
					) : (
						<FilePicker
							extensions={['pdf', 'docx', 'doc', 'png', 'tiff', 'jpg', 'jpeg']}
							onChange={onUpload}
							onError={(errMsg: any) => {
								console.log(errMsg)
								setUploadErrorShown(errMsg)
							}}
						>
							<UploadButton>
								{!!file ? 'Update resume' : 'Upload Word Document or PDF'}
							</UploadButton>
						</FilePicker>
					))}
			</FixedButtonHolder>
			<LottieLoaderHolder show={isLoading || (externalLoading as boolean)}>
				<LottieLoader />
			</LottieLoaderHolder>
			<Popup opened={uploadErrorShown}>
				<UploadWarning
					onDone={useCallback(() => {
						setUploadErrorShown(false)
					}, [uploadErrorShown])}
				>
					{getErrorType((uploadErrorShown as any) as string) === 'extension' ? (
						<>
							<h2>Change file format</h2>
							<p>
								To upload your resume, please select an image (JPG), Word, or
								PDF document format.
							</p>
						</>
					) : (
						<>
							<h2>Oh, wait….</h2>
							<p>{uploadErrorShown}</p>
						</>
					)}
				</UploadWarning>
			</Popup>
			<Popup opened={uploadWarningShown}>
				<UploadWarning
					onCancel={useCallback(() => {
						setUploadWarningShown(false)
					}, [setUploadWarningShown])}
                    onConfirm={() => (window as any).TempStars?.App?.goReact('hygienistHome') }
				>
					<h2>Oh, wait….</h2>
					<p>
						Resumes must be uploaded from a computer, not a mobile. Please log
						into your account from any computer to upload your resume!
					</p>
					<br />
					<br />
					<p>
					If you’re stuck or need help, please
					&nbsp;
                    <a onClick={() => (window as any).TempStars?.App?.goReact('hygienistHome') }>contact us</a>
					&nbsp;
					here and send us your resume. We can upload it for you!

					</p>
				</UploadWarning>
			</Popup>
		</Container>
	)
}
